import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useSubscriptionStore } from '@/store/useSubscriptionStore';
import { getDailyUsage, incrementUsage, canPerformAction, getTimeUntilReset, DailyUsage, ActionType } from '@/lib/dailyUsage';
import { toast } from 'sonner';

export function useDailyUsage() {
  const { user } = useAuth();
  const { isPremium, limits } = useSubscriptionStore();
  const [usage, setUsage] = useState<DailyUsage | null>(null);
  const [loading, setLoading] = useState(true);

  // Fetch current usage
  const fetchUsage = useCallback(async () => {
    if (!user) {
      setUsage(null);
      setLoading(false);
      return;
    }

    try {
      const currentUsage = await getDailyUsage(user.uid);
      setUsage(currentUsage);
    } catch (error) {
      console.error('Error fetching daily usage:', error);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchUsage();
  }, [fetchUsage]);

  // Check if action is allowed
  const checkAction = useCallback(async (
    action: ActionType
  ): Promise<{ allowed: boolean; remaining: number; message?: string; resetType?: 'daily' | 'weekly' }> => {
    if (!user) {
      return { allowed: false, remaining: 0, message: 'Please sign in' };
    }

    const premium = isPremium();
    return canPerformAction(user.uid, action, premium, limits);
  }, [user, isPremium, limits]);

  // Generic track function
  const trackAction = useCallback(async (
    action: ActionType,
    field: Parameters<typeof incrementUsage>[1],
    showUpgrade?: () => void
  ): Promise<boolean> => {
    if (!user) return false;

    const result = await checkAction(action);
    if (!result.allowed) {
      if (!isPremium() && showUpgrade) {
        showUpgrade();
      } else {
        toast.error(result.message);
      }
      return false;
    }

    await incrementUsage(user.uid, field);
    await fetchUsage();
    return true;
  }, [user, checkAction, fetchUsage, isPremium]);

  // Track usage and show upgrade dialog if needed
  const trackAutoAdd = useCallback(async (showUpgrade?: () => void): Promise<boolean> => {
    if (!user) return false;
    const premium = isPremium();
    const field = premium ? 'autoAddCount' : 'weeklyAutoAddCount';
    return trackAction('autoAdd', field, showUpgrade);
  }, [user, isPremium, trackAction]);

  const trackDownload = useCallback(async (showUpgrade?: () => void): Promise<boolean> => {
    return trackAction('download', 'downloadCount', showUpgrade);
  }, [trackAction]);

  const trackExport = useCallback(async (showUpgrade?: () => void): Promise<boolean> => {
    return trackAction('export', 'exportCount', showUpgrade);
  }, [trackAction]);

  const trackCompress = useCallback(async (showUpgrade?: () => void): Promise<boolean> => {
    return trackAction('compress', 'weeklyCompressCount', showUpgrade);
  }, [trackAction]);

  const trackFeedback = useCallback(async (showUpgrade?: () => void): Promise<boolean> => {
    return trackAction('feedback', 'feedbackCount', showUpgrade);
  }, [trackAction]);

  const trackInboxAI = useCallback(async (showUpgrade?: () => void): Promise<boolean> => {
    return trackAction('inboxAI', 'inboxAICount', showUpgrade);
  }, [trackAction]);

  // Get remaining counts
  const getRemainingAutoAdds = useCallback((): number => {
    if (isPremium()) {
      if (!usage) return limits.maxAutoAddPerDay;
      return Math.max(0, limits.maxAutoAddPerDay - usage.autoAddCount);
    }
    if (!usage) return limits.maxAutoAddPerWeek;
    return Math.max(0, limits.maxAutoAddPerWeek - usage.weeklyAutoAddCount);
  }, [usage, isPremium, limits]);

  const getRemainingDownloads = useCallback((): number => {
    if (isPremium()) return Infinity;
    if (!usage) return limits.maxDownloadsPerDay;
    return Math.max(0, limits.maxDownloadsPerDay - usage.downloadCount);
  }, [usage, isPremium, limits]);

  const getRemainingExports = useCallback((): number => {
    if (isPremium()) return Infinity;
    if (!usage) return limits.maxExportsPerDay;
    return Math.max(0, limits.maxExportsPerDay - usage.exportCount);
  }, [usage, isPremium, limits]);

  const getRemainingCompressions = useCallback((): number => {
    if (isPremium()) return Infinity;
    if (!usage) return limits.maxCompressPerWeek;
    return Math.max(0, limits.maxCompressPerWeek - usage.weeklyCompressCount);
  }, [usage, isPremium, limits]);

  const getRemainingFeedback = useCallback((): number => {
    const limit = isPremium() ? limits.maxFeedbackPerDay : limits.maxFeedbackPerDay;
    if (!usage) return limit;
    return Math.max(0, limit - usage.feedbackCount);
  }, [usage, isPremium, limits]);

  const getRemainingInboxAI = useCallback((): number => {
    if (!isPremium()) return 0;
    if (!usage) return limits.maxInboxAIPerDay;
    return Math.max(0, limits.maxInboxAIPerDay - usage.inboxAICount);
  }, [usage, isPremium, limits]);

  // Get reset timer string
  const getResetTimer = useCallback((type: 'daily' | 'weekly'): string => {
    return getTimeUntilReset(type);
  }, []);

  return {
    usage,
    loading,
    checkAction,
    trackAutoAdd,
    trackDownload,
    trackExport,
    trackCompress,
    trackFeedback,
    trackInboxAI,
    getRemainingAutoAdds,
    getRemainingDownloads,
    getRemainingExports,
    getRemainingCompressions,
    getRemainingFeedback,
    getRemainingInboxAI,
    getResetTimer,
    refetch: fetchUsage,
  };
}
